import {
  Box,
  Icon,
  Tab,
  TabContext,
  TabList,
  TabPanel,
  Theme,
  Typography,
} from "@outschool/backpack";
import {
  ActivityRatingAndReviewsQuery as ActivityRatingAndReviewsQueryType,
  ActivityRatingAndReviewsQueryVariables,
  ClassDetailsQuery_ActivityFragment,
  PublicReviewsOfLeaderQuery as PublicReviewsOfLeaderQueryType,
  PublicReviewsOfLeaderQueryVariables,
} from "@outschool/gql-frontend-generated";
import { faSparkles } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import {
  ActivityRatingAndReviewsQuery,
  ParentSubmittedPhotos,
  Reviews,
  publicReviewsOfLeaderQuery,
} from "@outschool/ui-components-website";
import { pxToRem } from "@outschool/ui-utils";
import React, { useEffect, useMemo, useRef } from "react";

import { ReviewRespondButton } from "../../reviews/ReviewRespondButton";

export enum TabValue {
  Class = "class",
  Leader = "leader",
  None = "none",
}

function ReviewsTabLabel({
  textLabel,
  reviewCount,
}: {
  textLabel: string;
  reviewCount: number | null;
}) {
  return (
    <Box flex>
      <Typography
        sx={(theme: Theme) => ({
          fontSize: pxToRem(22),
          color: "grey.900",
          typography: "h5",
          marginRight: pxToRem(5),
          [theme.breakpoints.down("md")]: {
            fontSize: pxToRem(16),
            marginRight: pxToRem(2),
            typography: "subTitle1",
          },
        })}
      >
        {textLabel}
      </Typography>
      <Typography
        sx={(theme: Theme) => ({
          fontSize: pxToRem(22),
          typography: "h5",
          color: "grey.500",
          [theme.breakpoints.down("md")]: {
            fontSize: pxToRem(16),
            typography: "subTitle1",
          },
        })}
      >
        ({reviewCount})
      </Typography>
    </Box>
  );
}

function useActivityReviews(activityUid?: string) {
  const { data, loading, fetchMore } = useQueryWithPreviousData<
    ActivityRatingAndReviewsQueryType,
    ActivityRatingAndReviewsQueryVariables
  >(ActivityRatingAndReviewsQuery, {
    skip: !activityUid,
    variables: {
      uid: activityUid!,
      offset: 0,
      limit: 6,
      skipAggregates: false,
      summaryVariant: "v2",
    },
  });

  const activity = data?.activity;

  return useMemo(
    () => ({
      loading,
      fetchMore,
      reviewCount: activity?.reviewCount ?? 0,
      reviewsWithTextCount: activity?.reviewsWithTextCount ?? 0,
      reviewsWithAttachments: activity?.reviewsWithAttachments ?? [],
      feedbackSummary: activity?.feedbackSummary ?? null,
      recentReviews: activity?.recentReviews ?? [],
    }),
    [activity, loading, fetchMore],
  );
}

export default function PublicReviewsOfActivity({
  activity,
  tabValue,
  setTabValue,
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  tabValue: TabValue;
  setTabValue: (tabValue: TabValue) => void;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\subcomponents\\PublicReviewsOfActivity",
  );

  const activityReviews = useActivityReviews(activity.uid);

  const tabInitialized = useRef(false);

  const {
    data: leaderData,
    loading: leaderDataLoading,
    fetchMore: leaderFetchMore,
  } = useQueryWithPreviousData<
    PublicReviewsOfLeaderQueryType,
    PublicReviewsOfLeaderQueryVariables
  >(publicReviewsOfLeaderQuery, {
    skip: !activity.leader.uid,
    variables: {
      leaderUid: activity.leader.uid,
      offset: 0,
      limit: 6,
      skipAggregates: false,
      includeSummary: true,
    },
  });

  const leader = leaderData?.publicProfile?.leader;
  const leaderReviewData = leader
    ? {
        recentReviews: leader.recentReviews ?? [],
        reviewsWithTextCount: leader.reviewsWithTextCount ?? 0,
        averageActivityStarRating: leader.averageActivityStarRating ?? 0,
        reviewCount: leader.reviewCount ?? 0,
        feedbackSummary: leader.feedbackSummary ?? null,
      }
    : {
        recentReviews: [],
        reviewsWithTextCount: 0,
        averageActivityStarRating: 0,
        reviewCount: 0,
        feedbackSummary: null,
      };

  const classReviewsTabActive = activityReviews.recentReviews.length > 0;
  const leaderReviewsTabActive = leaderReviewData.recentReviews.length > 0;

  const getAutoTabValue = (
    isClassReviewsTab: boolean,
    isLeaderReviewsTab: boolean,
  ) => {
    if (isClassReviewsTab) {
      return TabValue.Class;
    }
    if (isLeaderReviewsTab) {
      return TabValue.Leader;
    }
    return TabValue.None;
  };

  useEffect(() => {
    if (
      !activityReviews.loading &&
      !leaderDataLoading &&
      !tabInitialized.current
    ) {
      const autoTabValue = getAutoTabValue(
        classReviewsTabActive,
        leaderReviewsTabActive,
      );
      if (autoTabValue !== tabValue) {
        setTabValue(autoTabValue);
      }
      tabInitialized.current = true;
    }
  }, [
    activityReviews.loading,
    leaderDataLoading,
    classReviewsTabActive,
    leaderReviewsTabActive,
    setTabValue,
    tabValue,
  ]);

  const handleShowMoreClick = React.useCallback(async () => {
    if (!activityReviews.fetchMore || !activityReviews.recentReviews) {
      return;
    }
    try {
      await activityReviews.fetchMore({
        variables: {
          offset: activityReviews.recentReviews.length,
          limit: 6,
          skipAggregates: true,
        },
        updateQuery: (
          previousResult: ActivityRatingAndReviewsQueryType,
          { fetchMoreResult },
        ) => {
          if (!fetchMoreResult) {
            return previousResult;
          }
          return Object.assign({}, previousResult, {
            activity: {
              ...previousResult.activity,
              recentReviews: [
                ...previousResult.activity.recentReviews,
                ...fetchMoreResult.activity.recentReviews,
              ],
            },
          });
        },
      });
    } catch (e) {
      console.log("Warning: could not fetch more:", e);
    }
  }, [activityReviews]);

  const handleLeaderReviewShowMoreClick = React.useCallback(async () => {
    if (!leaderFetchMore || !leaderReviewData.recentReviews) {
      return;
    }
    try {
      await leaderFetchMore({
        variables: {
          offset: leaderReviewData.recentReviews.length,
          limit: 6,
          skipAggregates: true,
          includeSummary: false,
        },
        updateQuery: (
          leaderReviewPreviousResult: PublicReviewsOfLeaderQueryType,
          { fetchMoreResult: leaderFetchMore },
        ) => {
          if (!leaderFetchMore) {
            return leaderReviewPreviousResult;
          }

          const updatedLeader = Object.assign({}, leaderReviewPreviousResult, {
            publicProfile: {
              ...leaderReviewPreviousResult?.publicProfile,
              leader: {
                ...leaderReviewPreviousResult?.publicProfile?.leader,
                recentReviews: [
                  ...(leaderReviewPreviousResult?.publicProfile?.leader
                    ?.recentReviews ?? []),
                  ...(leaderFetchMore?.publicProfile?.leader?.recentReviews ??
                    []),
                ],
              },
            },
          });

          return updatedLeader;
        },
      });
    } catch (e) {
      console.log("Warning: could not fetch more:", e);
    }
  }, [leaderFetchMore, leaderReviewData.recentReviews]);

  const handleChange = (event: React.SyntheticEvent, tabValue: TabValue) => {
    setTabValue(tabValue);
  };

  if (
    activityReviews.recentReviews.length === 0 &&
    leaderReviewData.recentReviews.length === 0
  ) {
    if (tabValue === TabValue.None) {
      return (
        <Box sx={{ width: "100%", typography: "body1" }}>
          <Typography sx={{ marginY: 12 }}>
            {t("There are no reviews for this class yet.")}
          </Typography>
        </Box>
      );
    }
    return null;
  }

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      <TabContext value={tabValue || TabValue.Class}>
        <TabList
          onChange={handleChange}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            ".Mui-selected": { color: "black" },
            "& .MuiTabs-indicator": { backgroundColor: "black" },
          }}
        >
          {classReviewsTabActive && (
            <Tab
              label={
                <ReviewsTabLabel
                  textLabel={t("For this class")}
                  reviewCount={activityReviews.reviewCount}
                />
              }
              value={TabValue.Class}
              style={{
                paddingLeft: 0,
              }}
            />
          )}
          {leaderReviewsTabActive && (
            <Tab
              label={
                <ReviewsTabLabel
                  textLabel={t("For this teacher")}
                  reviewCount={leaderReviewData.reviewCount ?? null}
                />
              }
              value={TabValue.Leader}
              style={{
                paddingLeft: classReviewsTabActive ? pxToRem(12) : 0,
              }}
            />
          )}
        </TabList>
        {classReviewsTabActive && (
          <TabPanel
            value={TabValue.Class}
            sx={(theme: Theme) => ({
              paddingTop: 24,
              [theme.breakpoints.down("md")]: {
                paddingTop: undefined,
              },
            })}
          >
            <>
              <Typography
                sx={{ mb: 12, color: "grey.600" }}
                as="p"
                variant="body1"
              >
                {t(
                  "{{reviewCount}} total ratings, {{reviewsWithTextCount}} with reviews",
                  {
                    reviewCount: activityReviews.reviewCount,
                    reviewsWithTextCount: activityReviews.reviewsWithTextCount,
                  },
                )}
              </Typography>

              {activityReviews.feedbackSummary?.pros && (
                <FeedbackSummary
                  summary={activityReviews.feedbackSummary.pros}
                  title={t`Review summary for this class`}
                />
              )}

              {activityReviews.reviewsWithAttachments.length > 0 ? (
                <ParentSubmittedPhotos
                  reviewsWithAttachments={
                    activityReviews.reviewsWithAttachments
                  }
                />
              ) : null}
              <Reviews
                reviews={activityReviews.recentReviews}
                leader={activity.leader}
                handleShowMoreClick={handleShowMoreClick}
                ReviewRespondButton={ReviewRespondButton}
                reviewsWithTextCount={activityReviews.reviewsWithTextCount}
                recentReviewsCount={activityReviews.recentReviews.length}
              />
            </>
          </TabPanel>
        )}
        {leaderReviewsTabActive && (
          <TabPanel
            value={TabValue.Leader}
            sx={(theme: Theme) => ({
              marginTop: pxToRem(24),
              [theme.breakpoints.down("md")]: {
                marginTop: undefined,
              },
            })}
          >
            <Typography
              sx={{ mb: 12, color: "grey.600" }}
              as="p"
              variant="body1"
            >
              {t(
                "{{reviewCount}} total ratings, {{reviewsWithTextCount}} with reviews",
                {
                  reviewCount: leaderReviewData.reviewCount,
                  reviewsWithTextCount: leaderReviewData.reviewsWithTextCount,
                },
              )}
            </Typography>

            {leaderReviewData.feedbackSummary?.pros && (
              <FeedbackSummary
                summary={leaderReviewData.feedbackSummary.pros}
                title={t`Review summary for this teacher`}
              />
            )}

            <Reviews
              reviews={leaderReviewData.recentReviews}
              leader={activity.leader}
              handleShowMoreClick={handleLeaderReviewShowMoreClick}
              ReviewRespondButton={ReviewRespondButton}
              reviewsWithTextCount={leaderReviewData?.reviewsWithTextCount}
              recentReviewsCount={leaderReviewData?.recentReviews?.length}
              isLeaderReviewsTab
            />
          </TabPanel>
        )}
      </TabContext>
    </Box>
  );
}

function FeedbackSummary({
  summary,
  title,
}: {
  summary: string;
  title: string;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\subcomponents\\PublicReviewsOfActivity",
  );

  return (
    <>
      <Box
        sx={(theme: Theme) => ({
          marginTop: "1.25em",
          marginBottom: "0.5em",
          padding: "1em",
          borderRadius: "0.5em",
          backgroundColor: "primary.50",
          // align with review grid on xl size
          [theme.breakpoints.up("xl")]: {
            marginRight: "9px",
          },
        })}
      >
        <Typography as="div" variant="h6" sx={{ marginBottom: "0.5em" }}>
          <Icon icon={faSparkles} sx={{ marginRight: "0.5em" }} />
          {title}
        </Typography>
        <Typography as="div" variant="body1" sx={{ marginBottom: "0.5em" }}>
          {summary}
        </Typography>
        <Typography
          as="div"
          variant="caption"
        >{t`This summary was AI-generated from parent reviews`}</Typography>
      </Box>
    </>
  );
}
